import {
  DataSource,
  CdkTable,
  CdkColumnDef,
  CdkHeaderCellDef,
  CdkHeaderCell,
  CdkCellDef,
  CdkCell,
  CdkHeaderRowDef,
  CdkHeaderRow,
  CdkRowDef,
  CdkRow,
} from '@angular/cdk/table';
import { Component, effect, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RecordPermissionDialogComponent } from '../../dialogs/record-permission-dialog/record-permission-dialog.component';
import { IOption, IRecordAccess } from '@miq/shared-models';
import { DialogService } from '@miq/platform';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { L10nTranslationModule } from 'angular-l10n';
import { MatIcon } from '@angular/material/icon';
import { UpperCasePipe, TitleCasePipe } from '@angular/common';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-records-permissions',
  templateUrl: './records-permissions.component.html',
  styleUrl: './records-permissions.component.scss',
  imports: [
    L10nTranslationModule,
    MatIcon,
    CdkTable,
    CdkColumnDef,
    CdkHeaderCellDef,
    CdkHeaderCell,
    CdkCellDef,
    CdkCell,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatIconButton,
    CdkHeaderRowDef,
    CdkHeaderRow,
    CdkRowDef,
    CdkRow,
    UpperCasePipe,
    TitleCasePipe
],
})
export class RecordsPermissionsComponent implements OnInit {
  readonly recordsArrayInput = input<IRecordAccess[]>(undefined, { alias: 'recordsArray' });
  public recordsArray: IRecordAccess[]; // main records permissions array.
  public recordTypeOptions = input<IOption[]>([]); // record type options.
  public pageType = input<string>();
  public isPendingUser = input<boolean>();
  public displayedColumns: string[] = [
    // table displayed columns.
    'recordTypeVal',
    'recordID',
    'recordDesc',
    'actions',
  ];
  private readonly dataSubject = signal<IRecordAccess[]>([]); // signal used between component and table.
  public recordPermissionsDataSource: DataSource<IRecordAccess>; // table data source.
  public notificationService = inject(DialogService);
  constructor() {
    this.recordPermissionsDataSource = new RecordPermissionsDataSource(toObservable(this.dataSubject));
    effect(() => {
      const v = this.recordsArrayInput();
      if (v !== undefined) {
        this.recordsArray = v;
        this.dataSubject.set(this.recordsArray);
      }
    });
  }
  /**
   * Overrided ngOnInit method.
   */
  public ngOnInit() {
    const recordsArrayV = this.recordsArrayInput();
    if (recordsArrayV !== undefined) this.recordsArray = recordsArrayV;
    // If records array does not exists.
    if (!this.recordsArray) {
      // create an empty array.
      this.recordsArray = [];
      this.dataSubject.set(this.recordsArray);
    } else {
      // display array into the table.
      this.dataSubject.set(this.recordsArray);
    }
    if (this.pageType() === 'Admin' && this.isPendingUser()) {
      this.displayedColumns.splice(3, 0, 'Approve/Reject');
    }
  }
  /**
   * Add an empty record to the table.
   */
  public addRecord() {
    this.openDialog()
      .pipe(filter(result => !!result))
      .subscribe((result: IRecordAccess) => {
        // TODO: Request for record address.
        this.recordsArray.push(result);
        this.dataSubject.set(this.recordsArray);
      });
  }
  /**
   * Delete a record from the table using the
   * record ID to search for it.
   */
  public deleteRecord(recordToDelete) {
    const indexToDelete = this.recordsArray.map(record => record.recordID).indexOf(recordToDelete);
    this.recordsArray = [...this.recordsArray.slice(0, indexToDelete), ...this.recordsArray.slice(indexToDelete + 1)];
    this.dataSubject.set(this.recordsArray);
  }
  /**
   * Open dialog and return dialog reference.
   *
   * @param record Optional record parameter to edit.
   * @return Dialog reference.
   */
  private openDialog(record?: IRecordAccess): Observable<IRecordAccess> {
    const data = { recordTypesOptions: this.recordTypeOptions(), record: record || null };
    return this.notificationService.openDialog(RecordPermissionDialogComponent, data, { panelClass: 'table-modals' });
  }
}
class RecordPermissionsDataSource implements DataSource<IRecordAccess> {
  constructor(private source$: Observable<IRecordAccess[]>) {}
  /**
   * DataSource connect overrided method.
   */
  connect(): Observable<IRecordAccess[]> {
    return this.source$;
  }
  /**
   * DataSource disconnect overrided method.
   */
  disconnect() {
    /*
     * Nothing to be done on disconnect.
     */
  }
}
